import { FatalError } from "../services/ErrorHelpers";
import { IWeightages } from "../contexts/dataFetchers/actionLinks";

export interface IWeightageCalculatorArgs {
  entryId: number;
  rowWeightage: number;
}

export interface IWeightageCalculator {
  disableSaveButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disablePostButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableSendToPosterButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableValidateButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableApproveButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableNeedsClarificationButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableRecallButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableClearFormButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableAttachmentsButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableDeleteButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
  disableAddPostersButton(weightages: IWeightages, opts: IWeightageCalculatorArgs): boolean;
}

const WeighateCalculatorReturn = {
  disabled: true,
  enabled: false
};

function calculateRowAndButtonWeightage(buttonWeightage: number, rowWeightage?: number) {
  // tslint:disable-next-line: no-bitwise
  if (rowWeightage === undefined || rowWeightage === null) {
    throw new FatalError("No rowWeightage available");
  }
  if ((rowWeightage & buttonWeightage) !== buttonWeightage) {
    return WeighateCalculatorReturn.disabled;
  }
  return WeighateCalculatorReturn.enabled;
}

function getButtonWeightage(weightages: IWeightages, button: keyof IWeightages): number {
  if (!weightages) {
    console.error("No action weightages available.");
    return 0;
  }
  if (!(button in weightages)) {
    console.error(`No action named ${button} in Action Weightages.`);
    return 0;
  }

  return weightages[button];
}

/** Hear ye mortal
 *  The following code dictates the status on the Command Bar in SL Create
 *  It is in constant flux as requirements are being gathered
 *  All 3 arguments were needed for all buttons at some point, that changed
 *  then it changed back, and it might change again
 */
const WeightageCalculator: IWeightageCalculator = {
  disableSaveButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.enabled;
    const buttonWeightage = getButtonWeightage(weightages, "Save");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disablePostButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.enabled;
    const buttonWeightage = getButtonWeightage(weightages, "SubmitForReview");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableSendToPosterButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.enabled;
    const buttonWeightage = getButtonWeightage(weightages, "SendToPoster");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableValidateButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.enabled;
    const buttonWeightage = getButtonWeightage(weightages, "Validate");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableApproveButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.disabled;
    const buttonWeightage = getButtonWeightage(weightages, "ApproveSLEntries");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableNeedsClarificationButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.disabled;
    const buttonWeightage = getButtonWeightage(weightages, "NeedsClarification");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableRecallButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.disabled;
    const buttonWeightage = getButtonWeightage(weightages, "Recall");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableClearFormButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.enabled;
    const buttonWeightage = getButtonWeightage(weightages, "ClearForm");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableAttachmentsButton(weightages, { entryId, rowWeightage }) {
    if (entryId === 0) return WeighateCalculatorReturn.enabled;
    const buttonWeightage = getButtonWeightage(weightages, "Attachments");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableDeleteButton(weightages, { rowWeightage }) {
    const buttonWeightage = getButtonWeightage(weightages, "Delete");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  },
  disableAddPostersButton(weightages, { rowWeightage }) {
    const buttonWeightage = getButtonWeightage(weightages, "AddPosters");
    return calculateRowAndButtonWeightage(buttonWeightage, rowWeightage);
  }
};

export default WeightageCalculator;
